/**
 * PWA Service - Handles service worker registration and PWA features
 */

class PWAService {
  constructor() {
    this.swRegistration = null;
    this.deferredPrompt = null;
    this.isOnline = navigator.onLine;
    this.installable = false;
    
    this.init();
  }

  async init() {
    // Register service worker
    if ('serviceWorker' in navigator) {
      try {
        this.swRegistration = await navigator.serviceWorker.register('/sw.js');
        console.log('[PWA] Service Worker registered:', this.swRegistration);
        
        // Listen for updates
        this.swRegistration.addEventListener('updatefound', () => {
          const newWorker = this.swRegistration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New content is available, notify user
                this.notifyUpdate();
              }
            });
          }
        });
        
      } catch (error) {
        console.error('[PWA] Service Worker registration failed:', error);
      }
    }

    // Listen for install prompt
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('[PWA] Install prompt available');
      e.preventDefault();
      this.deferredPrompt = e;
      this.installable = true;
      
      // Notify app that install is available
      this.dispatchPWAEvent('installable', { canInstall: true });
    });

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      console.log('[PWA] App installed successfully');
      this.deferredPrompt = null;
      this.installable = false;
      
      this.dispatchPWAEvent('installed', { installed: true });
    });

    // Listen for online/offline status
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.dispatchPWAEvent('online', { online: true });
      console.log('[PWA] Back online');
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.dispatchPWAEvent('offline', { online: false });
      console.log('[PWA] Gone offline');
    });

    // Check if app is running in standalone mode
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      document.body.classList.add('pwa-standalone');
      console.log('[PWA] Running in standalone mode');
    }
  }

  // Show install prompt
  async showInstallPrompt() {
    if (!this.deferredPrompt) {
      console.log('[PWA] Install prompt not available');
      return false;
    }

    try {
      this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      
      console.log('[PWA] Install prompt outcome:', outcome);
      
      if (outcome === 'accepted') {
        this.dispatchPWAEvent('install-accepted');
      } else {
        this.dispatchPWAEvent('install-dismissed');
      }
      
      this.deferredPrompt = null;
      this.installable = false;
      
      return outcome === 'accepted';
    } catch (error) {
      console.error('[PWA] Install prompt error:', error);
      return false;
    }
  }

  // Check if app can be installed
  canInstall() {
    return this.installable && this.deferredPrompt !== null;
  }

  // Check if app is installed
  isInstalled() {
    return window.matchMedia('(display-mode: standalone)').matches || 
           window.navigator.standalone ||
           document.referrer.includes('android-app://');
  }

  // Get online status
  getOnlineStatus() {
    return this.isOnline;
  }

  // Force service worker update
  async updateServiceWorker() {
    if (!this.swRegistration) {
      console.log('[PWA] No service worker registration found');
      return false;
    }

    try {
      await this.swRegistration.update();
      console.log('[PWA] Service worker update initiated');
      return true;
    } catch (error) {
      console.error('[PWA] Service worker update failed:', error);
      return false;
    }
  }

  // Skip waiting and activate new service worker
  skipWaiting() {
    if (this.swRegistration && this.swRegistration.waiting) {
      this.swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }

  // Cache a new route
  cacheRoute(url) {
    if (this.swRegistration && this.swRegistration.active) {
      this.swRegistration.active.postMessage({ 
        type: 'CACHE_NEW_ROUTE', 
        url 
      });
    }
  }

  // Get cached data size (estimated)
  async getCacheSize() {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        return {
          used: estimate.usage,
          available: estimate.quota,
          percentage: Math.round((estimate.usage / estimate.quota) * 100)
        };
      } catch (error) {
        console.error('[PWA] Storage estimation failed:', error);
        return null;
      }
    }
    return null;
  }

  // Clear cache
  async clearCache() {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('[PWA] Cache cleared');
      return true;
    } catch (error) {
      console.error('[PWA] Cache clear failed:', error);
      return false;
    }
  }

  // Request notification permission
  async requestNotificationPermission() {
    if (!('Notification' in window)) {
      console.log('[PWA] Notifications not supported');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      console.log('[PWA] Notification permission denied');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('[PWA] Notification permission request failed:', error);
      return false;
    }
  }

  // Show local notification
  showNotification(title, options = {}) {
    if (Notification.permission !== 'granted') {
      console.log('[PWA] Notification permission not granted');
      return false;
    }

    const defaultOptions = {
      icon: '/android-icon-192x192.png',
      badge: '/android-icon-96x96.png',
      tag: 'prm-notification'
    };

    try {
      new Notification(title, { ...defaultOptions, ...options });
      return true;
    } catch (error) {
      console.error('[PWA] Notification failed:', error);
      return false;
    }
  }

  // Subscribe to push notifications
  async subscribeToPush(vapidPublicKey) {
    if (!this.swRegistration) {
      console.error('[PWA] No service worker registration');
      return null;
    }

    try {
      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
      });

      console.log('[PWA] Push subscription created:', subscription);
      return subscription;
    } catch (error) {
      console.error('[PWA] Push subscription failed:', error);
      return null;
    }
  }

  // Utility to convert VAPID key
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Store data for offline use
  async storeOfflineData(key, data) {
    try {
      localStorage.setItem(`prm_offline_${key}`, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
      return true;
    } catch (error) {
      console.error('[PWA] Offline storage failed:', error);
      return false;
    }
  }

  // Get offline data
  getOfflineData(key, maxAge = 24 * 60 * 60 * 1000) { // 24 hours default
    try {
      const stored = localStorage.getItem(`prm_offline_${key}`);
      if (!stored) return null;

      const { data, timestamp } = JSON.parse(stored);
      
      // Check if data is still valid
      if (Date.now() - timestamp > maxAge) {
        localStorage.removeItem(`prm_offline_${key}`);
        return null;
      }

      return data;
    } catch (error) {
      console.error('[PWA] Offline data retrieval failed:', error);
      return null;
    }
  }

  // Dispatch custom PWA events
  dispatchPWAEvent(type, detail = {}) {
    const event = new CustomEvent(`pwa:${type}`, { detail });
    window.dispatchEvent(event);
  }

  // Notify about app update
  notifyUpdate() {
    this.dispatchPWAEvent('update-available', {
      message: 'A new version of PRM Core is available!',
      action: () => this.skipWaiting()
    });
  }

  // Get app info
  getAppInfo() {
    return {
      isOnline: this.isOnline,
      canInstall: this.canInstall(),
      isInstalled: this.isInstalled(),
      hasServiceWorker: !!this.swRegistration,
      notificationPermission: 'Notification' in window ? Notification.permission : 'not-supported'
    };
  }
}

// Create singleton instance
const pwaService = new PWAService();

export default pwaService;